import { ref, shallowRef, unref, watch } from "vue";

import { useNotification } from "@/hooks/notifications";

import type {
  SpeechRecognitionErrorEvent,
  SpeechRecognitionEvent,
} from "@/models/voiceCommunication";

export function useSpeechRecognition(
  { lang, continuous, interimResults } = {
    lang: "en-US",
    continuous: true,
    interimResults: true,
  }
) {
  const isListening = ref(false);
  const isFinal = ref(false);
  const result = ref("");
  const error = shallowRef<string | undefined>(undefined);

  const { showMessage } = useNotification();

  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  // Checks if the browser supports the API
  const isSupported = Boolean(SpeechRecognition);
  // The speech recognition constructor
  const recognition = SpeechRecognition ? new SpeechRecognition() : null;

  const toggle = (value = isListening.value) => {
    isListening.value = value;
  };

  const start = () => {
    isListening.value = true;
  };

  const stop = () => {
    isListening.value = false;
  };

  if (isSupported && recognition) {
    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.lang = unref(lang);

    recognition.onstart = () => {
      isFinal.value = false;
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      // raw words that the user spoke
      const transcript = Array.from(event.results)
        .map((result) => {
          isFinal.value = result.isFinal;
          return result[0];
        })
        .map((result) => result.transcript)
        .join("");

      result.value = transcript;
      error.value = undefined;
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event?.error === "aborted") {
        return;
      }
      showMessage(event.error, "error");
    };

    recognition.onend = () => {
      isListening.value = false;
    };

    watch(isListening, () => {
      if (isListening.value && recognition)
        // Starting the speech recognition
        recognition.start();
      else if (recognition)
        // stopping the recognition
        recognition.stop();
    });
  }

  return {
    isSupported,
    isListening,
    isFinal,
    recognition,
    result,
    error,
    toggle,
    start,
    stop,
  };
}
